/** Super Connector / networking workflow task types stored in `public.tasks`. */

export const NETWORKING_TASK_TYPES = [
  'pre_screen',
  'review_profile',
  'enrich_profile',
  'linkedin_connect',
  'follow_up',
  'transcript_review',
  'custom',
] as const;

export type NetworkingTaskType = (typeof NETWORKING_TASK_TYPES)[number];

export const NETWORKING_TASK_STATUSES = [
  'pending',
  'in_progress',
  'done',
  'skipped',
  'canceled',
] as const;

export type NetworkingTaskStatus = (typeof NETWORKING_TASK_STATUSES)[number];

export const NETWORKING_TASK_SOURCES = ['booktime', 'calendly', 'manual', 'readai'] as const;

export type NetworkingTaskSource = (typeof NETWORKING_TASK_SOURCES)[number];

export const NETWORKING_TASK_SOURCE_LABELS: Record<NetworkingTaskSource, string> = {
  booktime: 'Calendar booking',
  calendly: 'Calendly',
  manual: 'Manual',
  readai: 'Read.ai transcript',
};

/** BookTime slug → display name when `booking_page_name` is missing. */
const BOOKTIME_SLUG_LABELS: Record<string, string> = {
  'intro-call': 'Intro call',
  'office-hours': 'Office hours',
  'clarity-check': 'Tech Stack Clarity Check',
  book: 'Intro call',
};

function formatWhen(iso: unknown): string | null {
  if (typeof iso !== 'string' || !iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function taskSourceLabel(source: string): string {
  if (source in NETWORKING_TASK_SOURCE_LABELS) {
    return NETWORKING_TASK_SOURCE_LABELS[source as NetworkingTaskSource];
  }
  return source;
}

/** Human-readable origin line (source + booking page + meeting time when available). */
export function taskOriginDescription(
  task: Pick<NetworkingTaskRow, 'source' | 'metadata'>,
): string {
  const meta = task.metadata ?? {};

  if (task.source === 'booktime') {
    const pageName =
      typeof meta.booking_page_name === 'string' && meta.booking_page_name.trim()
        ? meta.booking_page_name.trim()
        : null;
    const slug = typeof meta.booking_slug === 'string' ? meta.booking_slug : '';
    const page = pageName || (slug ? BOOKTIME_SLUG_LABELS[slug] ?? slug : null);
    const when = formatWhen(meta.booking_start);
    if (page && when) return `BookTime · ${page} · meeting ${when}`;
    if (page) return `BookTime · ${page}`;
    return 'BookTime calendar booking';
  }

  if (task.source === 'calendly') {
    const when = formatWhen(meta.booking_start);
    return when ? `Calendly · meeting ${when}` : 'Calendly meeting';
  }

  if (task.source === 'readai') {
    if (meta.origin === 'transcript_extraction') {
      const when = formatWhen(meta.meeting_date);
      return when ? `Extracted from call · ${when}` : 'Extracted from transcript';
    }
    const meetingTitle =
      typeof meta.meeting_title === 'string' && meta.meeting_title.trim()
        ? meta.meeting_title.trim()
        : null;
    const when = formatWhen(meta.meeting_date);
    if (meetingTitle && when) return `Read.ai · ${meetingTitle} · ${when}`;
    if (meetingTitle) return `Read.ai · ${meetingTitle}`;
    return 'Read.ai meeting transcript';
  }

  return taskSourceLabel(task.source);
}

export type TaskOriginTone = 'booktime' | 'calendly' | 'manual' | 'readai' | 'default';

export function taskOriginTone(source: string): TaskOriginTone {
  if (source === 'booktime') return 'booktime';
  if (source === 'calendly') return 'calendly';
  if (source === 'manual') return 'manual';
  if (source === 'readai') return 'readai';
  return 'default';
}

export const NETWORKING_TASK_TYPE_LABELS: Record<NetworkingTaskType, string> = {
  pre_screen: 'Pre-screen before call',
  review_profile: 'Review profile',
  enrich_profile: 'Enrich profile',
  linkedin_connect: 'LinkedIn connection',
  follow_up: 'Follow up',
  transcript_review: 'Review transcript',
  custom: 'Custom',
};

export const STRATEGY_SCORE_LABELS: Record<number, string> = {
  1: 'Park',
  2: 'Low pull',
  3: 'Optional',
  4: 'Worth it',
  5: 'Do now',
};

/** Short “why this band” copy for score modal + selects. */
export const STRATEGY_SCORE_DESCRIPTIONS: Record<number, string> = {
  1: 'Almost no $ / goals / network pull. Park it unless you write a work justification.',
  2: 'Thin leverage. Background only — justify if it jumps ahead of higher-scored work.',
  3: 'Mixed fit. Do when capacity is free; rescore when evidence gets clearer.',
  4: 'Clear pull on at least two axes. Lives in High value.',
  5: 'Direct line to money, current goals, or compounding network. Do now.',
};

/** Open tasks at or above this score land in the High value Kanban column. */
export const HIGH_VALUE_STRATEGY_SCORE_MIN = 4;

export function isHighValueStrategyScore(score: number | null | undefined): boolean {
  return typeof score === 'number' && score >= HIGH_VALUE_STRATEGY_SCORE_MIN;
}

/** Linked evidence that justifies strategy_score (stored on tasks.metadata.value_evidence). */
export const TASK_VALUE_RESOURCE_KINDS = [
  'subscriber',
  'transcript',
  'connection',
  'task',
  'directive',
  'brief',
  'article',
  'url',
  'other',
] as const;

export type TaskValueResourceKind = (typeof TASK_VALUE_RESOURCE_KINDS)[number];

export type TaskValueAxisScores = {
  /** Near-term cash / monetization path (0–3). */
  economic: number;
  /** Alignment to current goals / current-focus (0–3). */
  goals: number;
  /** ICP reach, intros, list, compounding network (0–3). */
  network: number;
};

export type TaskValueResource = {
  kind: TaskValueResourceKind;
  label: string;
  id?: string;
  url?: string;
  /** Why this resource moves $ / goals / network. */
  note?: string;
};

export type TaskValueContradiction = {
  task_id: string;
  title?: string;
  note: string;
  /** What we did: raised/lowered this score, updated peer, none. */
  action?: string;
};

export type TaskValueEvidence = {
  version: 1;
  axes: TaskValueAxisScores;
  strategy_score: number;
  summary: string;
  resources: TaskValueResource[];
  scored_at: string;
  contradictions?: TaskValueContradiction[];
  /** Who last set the score (hand icon when manual). */
  scored_by?: 'agent' | 'manual';
  /**
   * Why you’re doing the work even if the score is low / staying put.
   * Separate from rescoring — keeps the number honest while explaining the choice.
   */
  work_justification?: string;
  /**
   * Required when marking done — what value landed (UI + MCP same field).
   * Prefer writing via task_eval.completion_justification; mirrored here when closed.
   */
  completion_justification?: string;
};

/** Peer eval thread — UI chat and MCP agents append to the same blob. */
export type TaskEvalMessageRole = 'user' | 'peer' | 'system';

export type TaskEvalProposedPatch = {
  title?: string;
  due_at?: string;
  notes?: string;
  strategy_score?: number;
};

export type TaskEvalMessage = {
  id: string;
  role: TaskEvalMessageRole;
  content: string;
  at: string;
  /** Evidence cited in this turn (corpus, CRM, transcript, etc.). */
  sources?: TaskValueResource[];
  /** Field changes the peer wants — apply only after confirm. */
  proposed?: TaskEvalProposedPatch;
};

export type TaskEval = {
  version: 1;
  messages: TaskEvalMessage[];
  /** Why start / do low-score work (may mirror value_evidence.work_justification). */
  work_justification?: string;
  /** Required to mark done — outcome vs goals / ICP / $. */
  completion_justification?: string;
  updated_at: string;
};

export function emptyTaskEval(): TaskEval {
  return { version: 1, messages: [], updated_at: new Date().toISOString() };
}

export function readTaskEval(metadata: Record<string, unknown> | null | undefined): TaskEval {
  if (!metadata || typeof metadata !== 'object') return emptyTaskEval();
  const raw = metadata.task_eval;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return emptyTaskEval();
  const ev = raw as Record<string, unknown>;
  const messages = Array.isArray(ev.messages)
    ? (ev.messages as unknown[])
        .filter((m): m is Record<string, unknown> => !!m && typeof m === 'object' && !Array.isArray(m))
        .map((m) => ({
          id: typeof m.id === 'string' && m.id ? m.id : crypto.randomUUID(),
          role: (m.role === 'peer' || m.role === 'system' || m.role === 'user' ? m.role : 'user') as TaskEvalMessageRole,
          content: typeof m.content === 'string' ? m.content : '',
          at: typeof m.at === 'string' ? m.at : new Date().toISOString(),
          sources: Array.isArray(m.sources)
            ? (m.sources as unknown[])
                .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r))
                .map((r) => ({
                  kind: (TASK_VALUE_RESOURCE_KINDS.includes(r.kind as TaskValueResourceKind)
                    ? r.kind
                    : 'other') as TaskValueResourceKind,
                  label: typeof r.label === 'string' && r.label.trim() ? r.label.trim() : 'Resource',
                  id: typeof r.id === 'string' ? r.id : undefined,
                  url: typeof r.url === 'string' ? r.url : undefined,
                  note: typeof r.note === 'string' ? r.note : undefined,
                }))
            : undefined,
          proposed:
            m.proposed && typeof m.proposed === 'object' && !Array.isArray(m.proposed)
              ? (m.proposed as TaskEvalProposedPatch)
              : undefined,
        }))
        .filter((m) => m.content.trim())
    : [];

  return {
    version: 1,
    messages,
    work_justification:
      typeof ev.work_justification === 'string' && ev.work_justification.trim()
        ? ev.work_justification.trim()
        : undefined,
    completion_justification:
      typeof ev.completion_justification === 'string' && ev.completion_justification.trim()
        ? ev.completion_justification.trim()
        : undefined,
    updated_at: typeof ev.updated_at === 'string' ? ev.updated_at : new Date().toISOString(),
  };
}

export function withTaskEval(
  metadata: Record<string, unknown> | null | undefined,
  evalState: TaskEval,
): Record<string, unknown> {
  return {
    ...(metadata && typeof metadata === 'object' ? metadata : {}),
    task_eval: evalState,
  };
}

/** Done is blocked until a completion justification exists (eval thread or evidence mirror). */
export function taskHasCompletionJustification(
  task: Pick<NetworkingTaskRow, 'metadata'>,
): boolean {
  const evalState = readTaskEval(task.metadata);
  if (evalState.completion_justification) return true;
  const evidence = readTaskValueEvidence(task.metadata);
  return Boolean(evidence?.completion_justification);
}

function clampAxis(n: unknown): number {
  const v = typeof n === 'number' ? n : Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(3, Math.round(v)));
}

/** Roll axes 0–9 → strategy_score 1–5 (same rubric as agent scoring). */
export function strategyScoreFromAxes(axes: TaskValueAxisScores): number {
  const sum = clampAxis(axes.economic) + clampAxis(axes.goals) + clampAxis(axes.network);
  if (sum <= 1) return 1;
  if (sum <= 3) return 2;
  if (sum <= 5) return 3;
  if (sum <= 7) return 4;
  return 5;
}

export function readTaskValueEvidence(
  metadata: Record<string, unknown> | null | undefined,
): TaskValueEvidence | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const raw = metadata.value_evidence;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const ev = raw as Record<string, unknown>;
  const axesRaw = ev.axes;
  if (!axesRaw || typeof axesRaw !== 'object' || Array.isArray(axesRaw)) return null;
  const a = axesRaw as Record<string, unknown>;
  const axes: TaskValueAxisScores = {
    economic: clampAxis(a.economic),
    goals: clampAxis(a.goals),
    network: clampAxis(a.network),
  };
  const scoreRaw = typeof ev.strategy_score === 'number' ? ev.strategy_score : strategyScoreFromAxes(axes);
  const strategy_score = Math.max(1, Math.min(5, Math.round(scoreRaw)));
  const resources = Array.isArray(ev.resources)
    ? (ev.resources as unknown[])
        .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && !Array.isArray(r))
        .map((r) => ({
          kind: (TASK_VALUE_RESOURCE_KINDS.includes(r.kind as TaskValueResourceKind)
            ? r.kind
            : 'other') as TaskValueResourceKind,
          label: typeof r.label === 'string' && r.label.trim() ? r.label.trim() : 'Resource',
          id: typeof r.id === 'string' ? r.id : undefined,
          url: typeof r.url === 'string' ? r.url : undefined,
          note: typeof r.note === 'string' ? r.note : undefined,
        }))
    : [];
  const contradictions = Array.isArray(ev.contradictions)
    ? (ev.contradictions as unknown[])
        .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object' && !Array.isArray(c))
        .map((c) => ({
          task_id: typeof c.task_id === 'string' ? c.task_id : '',
          title: typeof c.title === 'string' ? c.title : undefined,
          note: typeof c.note === 'string' ? c.note : '',
          action: typeof c.action === 'string' ? c.action : undefined,
        }))
        .filter((c) => c.task_id && c.note)
    : undefined;

  const scored_by =
    ev.scored_by === 'agent' || ev.scored_by === 'manual' ? ev.scored_by : undefined;
  const work_justification =
    typeof ev.work_justification === 'string' && ev.work_justification.trim()
      ? ev.work_justification.trim()
      : undefined;
  const completion_justification =
    typeof ev.completion_justification === 'string' && ev.completion_justification.trim()
      ? ev.completion_justification.trim()
      : undefined;

  return {
    version: 1,
    axes,
    strategy_score,
    summary: typeof ev.summary === 'string' ? ev.summary : '',
    resources,
    scored_at: typeof ev.scored_at === 'string' ? ev.scored_at : '',
    contradictions: contradictions?.length ? contradictions : undefined,
    scored_by,
    work_justification,
    completion_justification,
  };
}

/** Manual origin (UI-created) — show hand next to score instead of a “Manual” pill. */
export function isManualTaskSource(source: string | null | undefined): boolean {
  return source === 'manual';
}

/** Score was last set by a human in the score modal / UI. */
export function isManuallyScoredTask(
  task: Pick<NetworkingTaskRow, 'metadata' | 'source'>,
): boolean {
  const evidence = readTaskValueEvidence(task.metadata);
  if (evidence?.scored_by === 'manual') return true;
  if (evidence?.scored_by === 'agent') return false;
  return isManualTaskSource(task.source);
}

export function withTaskValueEvidence(
  metadata: Record<string, unknown> | null | undefined,
  evidence: TaskValueEvidence,
): Record<string, unknown> {
  return {
    ...(metadata && typeof metadata === 'object' ? metadata : {}),
    value_evidence: evidence,
  };
}

/** Default pipeline order for a new relationship (BookTime seeds pre_screen on booking). */
export const DEFAULT_NETWORKING_PIPELINE: ReadonlyArray<{
  task_type: NetworkingTaskType;
  title: string;
  sort_order: number;
}> = [
  { task_type: 'pre_screen', title: 'Pre-screen before call', sort_order: 5 },
  { task_type: 'review_profile', title: 'Review profile', sort_order: 10 },
  { task_type: 'enrich_profile', title: 'Enrich profile', sort_order: 20 },
  { task_type: 'linkedin_connect', title: 'LinkedIn connection', sort_order: 30 },
  { task_type: 'follow_up', title: 'Follow up', sort_order: 40 },
];

export interface NetworkingTaskRow {
  id: string;
  subscriber_id: string | null;
  transcript_id: string | null;
  contact_email: string;
  task_type: NetworkingTaskType;
  title: string;
  status: NetworkingTaskStatus;
  due_at: string;
  completed_at: string | null;
  source: NetworkingTaskSource;
  notes: string | null;
  metadata: Record<string, unknown>;
  sort_order: number;
  strategy_score: number | null;
  /** Plan this task sits on (like arc for ideas). */
  epic_id: string | null;
  /** suggested → tentative → locked (CEO confirm). */
  epic_placement: EpicPlacement | null;
  created_at: string;
  updated_at: string;
  subscriber?: { id: string; name: string | null; email: string | null } | null;
  transcript?: { id: string; title: string | null; meeting_date: string | null } | null;
  epic?: WorkEpicRow | null;
}

export type EpicPlacement = 'suggested' | 'tentative' | 'locked';

export const EPIC_PLACEMENTS: readonly EpicPlacement[] = ['suggested', 'tentative', 'locked'];

export const EPIC_PLACEMENT_LABELS: Record<EpicPlacement, string> = {
  suggested: 'Suggested',
  tentative: 'Tentative',
  locked: 'Locked',
};

export const WORK_EPIC_STATUSES = ['active', 'paused', 'done', 'archived'] as const;

export type WorkEpicStatus = (typeof WORK_EPIC_STATUSES)[number];

export interface WorkEpicRow {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  target_date: string | null;
  status: WorkEpicStatus;
  /** Plan-level bet 1–5 (why this epic exists). */
  strategy_score: number | null;
  /** Short why: $ / goals / network. */
  value_note: string | null;
  sort_order: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export const DEFAULT_OPS_INBOX_EPIC_SLUG = 'ops-inbox';

/** Kebab slug for work_epics.slug — matches MCP + eval-board create. */
export function slugifyEpicTitle(title: string): string {
  const base = title
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return base || `epic-${Date.now().toString(36)}`;
}

export function normalizeEpicPlacement(raw: string | null | undefined): EpicPlacement | null {
  if (raw === 'suggested' || raw === 'tentative' || raw === 'locked') return raw;
  return null;
}

export function isTranscriptTask(task: Pick<NetworkingTaskRow, 'task_type' | 'transcript_id'>): boolean {
  return task.task_type === 'transcript_review' || Boolean(task.transcript_id);
}

/** Task created by LLM transcript extraction (metadata.origin = transcript_extraction). */
export function isTranscriptExtractedTask(
  task: Pick<NetworkingTaskRow, 'metadata' | 'task_type'>,
): boolean {
  return (
    task.task_type !== 'transcript_review' &&
    task.metadata?.origin === 'transcript_extraction'
  );
}

export function isTranscriptLinkedTask(task: Pick<NetworkingTaskRow, 'transcript_id'>): boolean {
  return Boolean(task.transcript_id);
}

export function isOpenTaskStatus(status: string): boolean {
  return status === 'pending' || status === 'in_progress';
}

export function taskTypeLabel(taskType: string): string {
  if (taskType in NETWORKING_TASK_TYPE_LABELS) {
    return NETWORKING_TASK_TYPE_LABELS[taskType as NetworkingTaskType];
  }
  return taskType;
}

export function isTaskOverdue(dueAt: string, status: string, nowMs = Date.now()): boolean {
  if (!isOpenTaskStatus(status)) return false;
  const dueMs = new Date(dueAt).getTime();
  return !Number.isNaN(dueMs) && dueMs < nowMs;
}

/** Patch for `tasks` status changes (reopen clears `completed_at`). */
export function taskStatusUpdatePatch(status: NetworkingTaskStatus): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    status,
    updated_at: new Date().toISOString(),
  };
  if (status === 'done') {
    patch.completed_at = new Date().toISOString();
  } else {
    patch.completed_at = null;
  }
  return patch;
}

export function isClosedTaskStatus(status: string): boolean {
  return status === 'done' || status === 'skipped' || status === 'canceled';
}

/** Eval board columns — alignment by score, not a todo inbox. */
export const TASK_KANBAN_COLUMNS = [
  {
    id: 'high_value',
    label: 'High value',
    hint: 'Score 4–5 — do these; glued to $ · goals · network',
    statuses: ['pending', 'in_progress'] as const,
    accent: 'border-violet-300 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-950/20',
    header: 'bg-violet-100 dark:bg-violet-900/40 text-violet-900 dark:text-violet-100',
    /** Score-bucket columns are views — drop opens eval, does not invent a score. */
    dropMode: 'eval' as const,
  },
  {
    id: 'needs_score',
    label: 'Needs score',
    hint: 'Unscored — open chat before you treat it as work',
    statuses: ['pending', 'in_progress'] as const,
    accent: 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20',
    header: 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100',
    dropMode: 'eval' as const,
  },
  {
    id: 'low_pull',
    label: 'Low pull',
    hint: 'Score 1–3 — raise value in chat, justify, or park',
    statuses: ['pending', 'in_progress'] as const,
    accent: 'border-rose-300 dark:border-rose-800 bg-rose-50/40 dark:bg-rose-950/20',
    header: 'bg-rose-100 dark:bg-rose-900/40 text-rose-900 dark:text-rose-100',
    dropMode: 'eval' as const,
  },
  {
    id: 'done',
    label: 'Done',
    hint: 'Completed — needs completion justification',
    statuses: ['done'] as const,
    accent: 'border-emerald-300 dark:border-emerald-700 bg-emerald-50/50 dark:bg-emerald-950/20',
    header: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-900 dark:text-emerald-100',
    dropMode: 'status' as const,
  },
  {
    id: 'archived',
    label: 'Archived',
    hint: 'Skipped or canceled',
    statuses: ['skipped', 'canceled'] as const,
    accent: 'border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-900/30',
    header: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200',
    dropMode: 'status' as const,
  },
] as const;

export type TaskKanbanColumnId = (typeof TASK_KANBAN_COLUMNS)[number]['id'];

/** Status written when a card is dropped on a status column (not score buckets). */
export function kanbanDropStatus(columnId: TaskKanbanColumnId): NetworkingTaskStatus | null {
  if (columnId === 'archived') return 'skipped';
  if (columnId === 'done') return 'done';
  // Score buckets are eval views — status unchanged via drop.
  return null;
}
